import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SopAPI } from '../../../utils/api';
import { BlockRenderer } from '../renderer';
import { findBlock } from '../utils/blockUtils';
import {
  normalizeSourceBlockIds,
  snippetPreviewText,
  snippetTypeLabel,
  truncatePreview,
} from '../utils/snippetUtils';
import { SnippetBlockPicker } from '../components/SnippetBlockPicker';
import { SnippetPreviewModal } from '../components/SnippetPreviewModal';
import { renderIcon } from '../../SprintDetail/FlowchartView/utils/renderIcon';

const noop = () => {};

/**
 * SnippetBlock — embed one or more blocks from another doc by reference.
 *
 * props: {
 *   sourceDocId,
 *   sourceBlockIds: string[],  // preferred (multi)
 *   sourceBlockId?,            // legacy single
 *   sourceDocTitle?, previewText?, sourceBlockType?
 * }
 */
export const SnippetBlock = ({
  block,
  onUpdate,
  onDelete,
  selectedBlockId,
  onOpenDoc,
  resolveDocMeta,
  availableDocs,
  resolveVariable,
}) => {
  const props = block.props || {};
  const { sourceDocId } = props;
  const sourceBlockIds = normalizeSourceBlockIds(props);
  const selected = selectedBlockId === block.id;

  const [status, setStatus] = useState(
    sourceDocId && sourceBlockIds.length ? 'loading' : 'empty'
  ); // empty | loading | ready | missing
  const [sourceBlocks, setSourceBlocks] = useState([]);
  const [sourceDocTitle, setSourceDocTitle] = useState(
    props.sourceDocTitle || ''
  );
  const [sourceDocIcon, setSourceDocIcon] = useState(null);
  const [previewText, setPreviewText] = useState(props.previewText || '');
  const [showPicker, setShowPicker] = useState(
    !sourceDocId || !sourceBlockIds.length
  );
  const [showModal, setShowModal] = useState(false);
  const [pickerPos, setPickerPos] = useState({ x: 80, y: 120 });
  const cardRef = useRef(null);

  useLayoutEffect(() => {
    if (!showPicker || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    setPickerPos({ x: r.left, y: r.bottom + 6, caretTop: r.top });
  }, [showPicker, status]);

  const applyResolved = useCallback(
    (doc, foundBlocks) => {
      const title = doc?.title || 'Untitled Doc';
      const first = foundBlocks[0];
      const preview = truncatePreview(snippetPreviewText(first));
      setSourceBlocks(foundBlocks);
      setSourceDocTitle(title);
      setSourceDocIcon(doc?.icon);
      setPreviewText(preview);
      setStatus('ready');

      const ids = foundBlocks.map((b) => b.id);
      const nextProps = {
        ...block.props,
        sourceDocId: doc.id,
        sourceBlockIds: ids,
        sourceBlockId: ids[0],
        sourceDocTitle: title,
        sourceBlockType: first.type,
        previewText: preview,
      };
      const prevIds = normalizeSourceBlockIds(block.props).join('\0');
      const changed =
        block.props?.sourceDocTitle !== title ||
        block.props?.sourceBlockType !== first.type ||
        block.props?.previewText !== preview ||
        prevIds !== ids.join('\0');
      if (changed) {
        onUpdate?.(block.id, { props: nextProps });
      }
    },
    [block.id, block.props, onUpdate]
  );

  const idsKey = sourceBlockIds.join('\0');

  useEffect(() => {
    if (!sourceDocId || !sourceBlockIds.length) {
      setStatus('empty');
      setSourceBlocks([]);
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    SopAPI.get(sourceDocId)
      .then((doc) => {
        if (cancelled) return;
        if (!doc || doc.deletedAt) {
          setStatus('missing');
          setSourceBlocks([]);
          return;
        }
        const found = sourceBlockIds
          .map((id) => findBlock(doc.body || [], id))
          .filter(Boolean);
        if (!found.length) {
          setStatus('missing');
          setSourceBlocks([]);
          const metaTitle =
            resolveDocMeta?.(sourceDocId)?.title ||
            props.sourceDocTitle ||
            doc.title ||
            'Untitled Doc';
          setSourceDocTitle(metaTitle);
          setSourceDocIcon(doc.icon);
          return;
        }
        applyResolved(doc, found);
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('missing');
        setSourceBlocks([]);
        const meta = resolveDocMeta?.(sourceDocId);
        if (meta?.title) setSourceDocTitle(meta.title);
        if (meta?.icon) setSourceDocIcon(meta.icon);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceDocId, idsKey]);

  useEffect(() => {
    if (!sourceDocId || status !== 'ready') return;
    const meta = resolveDocMeta?.(sourceDocId);
    if (meta?.title && meta.title !== sourceDocTitle) {
      setSourceDocTitle(meta.title);
      onUpdate?.(block.id, {
        props: { ...block.props, sourceDocTitle: meta.title },
      });
    }
    if (meta?.icon) setSourceDocIcon(meta.icon);
  }, [
    sourceDocId,
    status,
    resolveDocMeta,
    sourceDocTitle,
    block.id,
    block.props,
    onUpdate,
  ]);

  const handlePick = (picked) => {
    const ids = picked.sourceBlockIds?.length
      ? picked.sourceBlockIds
      : picked.sourceBlockId
        ? [picked.sourceBlockId]
        : [];
    onUpdate?.(block.id, {
      props: {
        sourceDocId: picked.sourceDocId,
        sourceBlockIds: ids,
        sourceBlockId: ids[0] || null,
        sourceDocTitle: picked.sourceDocTitle,
        sourceBlockType: picked.sourceBlockType,
        previewText: picked.previewText,
      },
    });
    setShowPicker(false);
    setSourceBlocks(picked.sourceBlocks || []);
    setSourceDocTitle(picked.sourceDocTitle || '');
    setSourceDocIcon(picked.sourceDocIcon);
    setPreviewText(picked.previewText || '');
    setStatus(picked.sourceBlocks?.length ? 'ready' : 'loading');
  };

  const openPicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowPicker(true);
  };

  const handleCardClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (status === 'ready' && sourceBlocks.length) {
      setShowModal(true);
      return;
    }
    if (status === 'empty' || status === 'missing') {
      openPicker(e);
    }
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(e);
    }
  };

  const liveMeta = sourceDocId ? resolveDocMeta?.(sourceDocId) : null;
  const displayTitle = liveMeta?.title || sourceDocTitle || 'Untitled Doc';
  const displayIcon = liveMeta?.icon ?? sourceDocIcon;
  const first = sourceBlocks[0];
  const sectionName =
    sourceBlocks.length > 1
      ? `${previewText && previewText !== '(empty)' ? previewText : snippetTypeLabel(first)} (+${sourceBlocks.length - 1} more)`
      : (previewText && previewText !== '(empty)' ? previewText : null) ||
        (first ? snippetTypeLabel(first) : 'Untitled');

  if (status === 'missing') {
    return (
      <div
        ref={cardRef}
        className={[
          'snippet-block',
          'snippet-block--error',
          selected ? 'snippet-block--selected' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        contentEditable={false}
        role="alert"
      >
        <div className="snippet-block__error-title">Source was deleted</div>
        <div className="snippet-block__error-msg">
          {displayTitle
            ? `This content is no longer in “${displayTitle}”.`
            : 'The embedded content could not be found.'}
        </div>
        <div className="snippet-block__actions">
          <button
            type="button"
            className="snippet-block__action snippet-block__action--primary"
            onClick={openPicker}
          >
            Choose another
          </button>
          <button
            type="button"
            className="snippet-block__action"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(block.id);
            }}
          >
            Remove
          </button>
        </div>
        {showPicker && (
          <SnippetBlockPicker
            position={pickerPos}
            onPick={handlePick}
            onClose={() => setShowPicker(false)}
            availableDocs={availableDocs}
          />
        )}
      </div>
    );
  }

  if (status === 'empty') {
    return (
      <div
        ref={cardRef}
        className={[
          'snippet-block',
          'snippet-block--empty',
          selected ? 'snippet-block--selected' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        contentEditable={false}
      >
        <div className="snippet-block__placeholder">Embed content from another doc</div>
        <div className="snippet-block__actions">
          <button
            type="button"
            className="snippet-block__action snippet-block__action--primary"
            onClick={openPicker}
          >
            Choose content
          </button>
          <button
            type="button"
            className="snippet-block__action"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(block.id);
            }}
          >
            Remove
          </button>
        </div>
        {showPicker && (
          <SnippetBlockPicker
            position={pickerPos}
            onPick={handlePick}
            onClose={() => setShowPicker(false)}
            availableDocs={availableDocs}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div
        ref={cardRef}
        role="button"
        tabIndex={0}
        className={[
          'snippet-block',
          'snippet-block--card',
          status === 'loading' ? 'snippet-block--loading' : '',
          selected ? 'snippet-block--selected' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        contentEditable={false}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        title="Click to view snippet"
      >
        <div className="snippet-block__peek doc-editor doc-editor--readonly">
          <div className="snippet-block__peek-content" aria-hidden="true">
            {sourceBlocks.length ? (
              <div className="doc-editor-blocks">
                {sourceBlocks.map((b, i) => (
                  <BlockRenderer
                    key={b.id}
                    block={b}
                    prevType={sourceBlocks[i - 1]?.type}
                    nextType={sourceBlocks[i + 1]?.type}
                    onUpdate={noop}
                    onInsertAfter={noop}
                    onUpdateAndInsert={noop}
                    onDelete={noop}
                    onMergeWithPrevious={noop}
                    onMergeWithNext={noop}
                    onNavigate={noop}
                    onEscapeContainer={noop}
                    onOpenDoc={noop}
                    resolveDocMeta={resolveDocMeta}
                    resolveVariable={resolveVariable}
                    listNumber={b.type === 'number' ? 1 : undefined}
                  />
                ))}
              </div>
            ) : (
              <div className="snippet-block__peek-skeleton">
                {previewText || 'Loading snippet…'}
              </div>
            )}
            {/* Fades UP from opaque (meets caption) into the teaser */}
            <div className="snippet-block__peek-fade" />
          </div>
          <div className="snippet-block__footer">
            <div className="snippet-block__caption">
              <div className="snippet-block__caption-line">
                <span className="snippet-block__caption-label">Snippet:</span>{' '}
                <span className="snippet-block__caption-name">{sectionName}</span>
              </div>
              <div className="snippet-block__caption-line">
                <span className="snippet-block__caption-label">Document:</span>{' '}
                <span className="snippet-block__caption-doc">
                  <span className="snippet-block__caption-doc-icon" aria-hidden="true">
                    {renderIcon(displayIcon, {
                      size: '13px',
                      fallback: <span>📄</span>,
                    })}
                  </span>
                  <span className="snippet-block__caption-doc-title">{displayTitle}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Centered on the whole card — open / expand (box + arrow out) */}
        <span className="snippet-block__expand" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M14 4h6v6"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20 4L11 13"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18 14v4.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      {showPicker && (
        <SnippetBlockPicker
          position={pickerPos}
          onPick={handlePick}
          onClose={() => setShowPicker(false)}
          availableDocs={availableDocs}
        />
      )}
      {showModal && sourceBlocks.length > 0 && (
        <SnippetPreviewModal
          sourceDocId={sourceDocId}
          sourceDocTitle={displayTitle}
          sourceDocIcon={displayIcon}
          sourceBlocks={sourceBlocks}
          onClose={() => setShowModal(false)}
          onOpenDoc={onOpenDoc}
          resolveDocMeta={resolveDocMeta}
          resolveVariable={resolveVariable}
        />
      )}
    </>
  );
};
